<template>
    <div class="fixed-player-info" v-if="block" :style="changeDurationToggleVisible ? 'padding-bottom: 10px;' : null">
        <div v-if="canSetVolume" class="volume-control">
            <div class="d-flex align-items-center">
                <div class="mr-2">{{ $t('general.volume') }}</div>
                <div>
                    <button class="btn btn-primary" :disabled="currentVolume <= 0" @click="setVolume(currentVolume - 10)">
                        <icon name="minus" />
                    </button>
                </div>
                <div class="ml-2 mr-2 font-weight-bold d-flex align-items-center">
                    <div class="volume-slider-container">
                        <input
                            :value="currentVolume"
                            type="range"
                            min="0"
                            max="100"
                            class="volume-slider"
                            @change="setVolume($event.target.value)"
                        />
                        <span class="volume-slider-text">{{ currentVolume }}%</span>
                    </div>
                </div>
                <div>
                    <button class="btn btn-primary" :disabled="currentVolume >= 100" @click="setVolume(currentVolume + 10)">
                        <icon name="plus" />
                    </button>
                </div>
            </div>
        </div>

        <div class="row player-actions-info">
            <div class="col-12">
                <span class="name h6 block-name">{{ block.name.toUpperCase() }}<span v-if="isRepeating"> - {{ $t('general.repeat') }}</span></span>
            </div>
            <div class="col-12 col-sm-3 align-items-center d-none d-sm-flex">
                <div class="block-info">
                    <span v-if="blockType === 1" class="badge badge-info">{{ $t('blocks.photo') }}</span>
                    <span v-if="blockType === 2" class="badge badge-primary">{{ $t('blocks.video') }}</span>
                    <span v-if="blockType === 3" class="badge badge-dark">{{ $t('blocks.type_speaker') }}</span>
                </div>
            </div>
            <div class="col-sm-6 d-flex align-items-center justify-content-center">
                <div class="player-actions">
                    <button class="btn btn-sm btn-company" @click="blockActionDebounced('resume')" v-if="!isPlaying()">
                        <icon name="play"></icon>
                    </button>

                    <button class="btn btn-sm btn-company" @click="blockActionDebounced('pause')" v-if="isPlaying()">
                        <icon name="pause"></icon>
                    </button>

                    <button class="btn btn-sm btn-company" @click="blockActionDebounced('stop')">
                        <icon name="stop"></icon>
                    </button>
                </div>
            </div>
            <div class="col-12 col-sm-3 align-items-center d-none d-sm-flex">
                <div class="playing-info">
                    <span v-if="block.files.length && fileCount">{{ blockType === 1 ? $t('blocks.photo') : $t('blocks.video') }} <strong>{{ fileCount }}/{{ totalFiles }}</strong></span>
                </div>
            </div>
        </div>

        <div class="player-progress-bar" v-if="!isInfiniteBlock(block, isRepeating)">
            <div class="progress-bar-text progress-bar-start">{{ $root.minutesToTimeInput(currentDuration) }}</div>
            <div class="progress-bar-visual" :class="setDurationEnabled && canSetDuration ? 'set-duration-enabled' : null" ref="progressBar" @click="onProgressBarClick">
                <div class="progress-bar-content" :class="noAnimation ? 'no-animation' : null" :style="{ width: progressWidth }" />

                <div v-if="setDurationEnabled && canSetDuration" class="progress-bar-content progress-bar-content-dragging" :class="noAnimation ? 'no-animation' : null" :style="{ width: progressDragWidth }">
                    <span
                        class="progress-bar-handle"
                        @mousedown="dragStart"
                        @touchstart="dragStart"
                    >
                        <span class="progress-bar-handle-value" v-if="dragX">
                            {{ $root.minutesToTimeInput(dragDuration) }}
                        </span>
                    </span>
                </div>
            </div>
            <div class="progress-bar-text progress-bar-end">{{ $root.minutesToTimeInput(totalDuration) }}</div>
        </div>

        <div
            v-if="changeDurationToggleVisible"
            class="custom-control custom-switch"
            style="margin-top: 15px;"
        >
            <input
                type="checkbox"
                class="custom-control-input"
                id="toggle_set_duration"
                style="cursor: pointer"
                v-model="canChangeDuration"
            >
            <label class="custom-control-label" for="toggle_set_duration">{{ $t('blocks.timeline_control') }}</label>
        </div>
    </div>
</template>

<script>

    import getImageForDuration from '../utils/getImageForDuration';
    import getVideoForDuration from '../utils/getVideoForDuration';
    import { EventBus } from '../utils/eventBus';
    import { getMaxDurationFromBlock, isInfiniteBlock } from '../utils/getMaxDurationForBlock';
    import { isMobile, isMobileOrTablet } from '../utils/deviceDetect';
    import { PLAYER_COMMUNICATION } from '../enums/player-communication';
    import { PLAYER_STATE } from '../enums/player-state';
    import { mapState } from 'vuex';
    import { debounce } from 'lodash';

    const deviceCheck = {
        desktop() {
            return !isMobileOrTablet();
        },
        tablet() {
            return isMobileOrTablet();
        },
        mobile() {
            return isMobile();
        },
    };

    export default {

        props: {
            currentDuration: {
                required: true,
                type: Number,
            },

            currentFileIndex: {
                required: true,
                type: Number | null,
            },

            currentVolume: {
                type: Number,
                default: 100,
            },

            state: {
                required: true,
                type: String | null,
            },

            block: {
                required: true,
                type: Object | null,
            },

            ceremony: {
                required: true,
                type: Object | null,
            },

            noAnimation: {
                type: Boolean,
                default: false,
            },

            communication: {
                type: String,
                default: PLAYER_COMMUNICATION.WEBSOCKET,
            },

            isRepeating: {
                type: Boolean,
                default: false,
            },

            fixedBlock: {
                type: Object | null,
                default: null,
            },

            maxDuration: {
                type: Number,
                default: null,
            },
        },

        watch: {
            ceremony: {
                deep: true,
                handler() {
                    this.canChangeDuration = false;
                },
            },
        },

        computed: {
            progressWidth() {
                const max = this.totalDuration;

                if (max === 0 && this.currentDuration === 0) {
                    return '0';
                }

                const percentage = this.currentDuration / (max / 100);

                if (percentage <= 0) {
                    return '0';
                }

                if (percentage >= 100) {
                    return '100%';
                }

                return percentage.toFixed(2) + '%';
            },

            dragDuration() {
                if (!this.$refs.progressBar || !this.dragX) {
                    return this.currentDuration;
                }

                const max = this.totalDuration;

                if (max === 0 && this.currentDuration === 0) {
                    return '0';
                }

                const offsetLeft = this.$refs.progressBar.offsetLeft;
                const width = this.$refs.progressBar.clientWidth;
                const mouseX = this.dragX - offsetLeft;

                const barPercentage = (mouseX <= width ? mouseX : width) / (width / 100);

                if (barPercentage <= 0) {
                    return 0;
                }

                return Math.floor((max / 100) * barPercentage);
            },

            progressDragWidth() {
                if (!this.$refs.progressBar || !this.dragX) {
                    return this.progressWidth;
                }

                const offsetLeft = this.$refs.progressBar.offsetLeft;
                const width = this.$refs.progressBar.clientWidth;
                const mouseX = this.dragX - offsetLeft;

                if (mouseX <= 0) {
                    return '0';
                }

                if (mouseX >= width) {
                    return '100%';
                }

                return mouseX + 'px';
            },

            totalFiles() {
                if (!this.block) {
                    return 0;
                }

                return !this.block.parent_id ? this.block.subblocks[0].files.length : this.block.files.length;
            },

            totalDuration() {
                if (this.maxDuration !== null) {
                    return this.maxDuration;
                }

                return Number(parseInt(getMaxDurationFromBlock(this.ceremony, this.block, this.isRepeating)));
            },

            fileCount() {
                if (!this.block) {
                    return null;
                }

                if (this.block.has_manual_action) {
                    return (this.currentFileIndex) ? (this.currentFileIndex + 1) : 1;
                }

                const block = this.block.parent_id ? this.block : this.block.subblocks[0];

                if ((!this.block.parent_id && this.block.block_type === 1) || this.block.type === 1) {
                    return getImageForDuration(this.ceremony, block, block.files, this.currentDuration, this.isRepeating) + 1;
                }

                if ((!this.block.parent_id && this.block.block_type === 2) || this.block.type === 2) {
                    return getVideoForDuration(block, block.files, this.currentDuration) + 1;
                }

                return null;
            },

            blockType() {
                if (!this.block) {
                    return null;
                }

                return this.block.block_type || this.block.type;
            },

            setDurationEnabled() {
                if (BUILD_ELECTRON) {
                    return true;
                }

                if (!this.companySettings?.set_duration_enabled) {
                    return false;
                }

                if (!this.companySettings?.set_duration_devices.length) {
                    return true;
                }

                return this.companySettings.set_duration_devices.some(device => {
                    if (!deviceCheck.hasOwnProperty(device)) {
                        return false;
                    }

                    return deviceCheck[device]();
                });
            },

            setDurationConfirmationEnabled() {
                if (BUILD_ELECTRON) {
                    return true;
                }

                return this.companySettings?.set_duration_confirmation_enabled;
            },

            changeDurationToggleVisible() {
                if (BUILD_ELECTRON) {
                    return true;
                }

                return (
                    this.setDurationConfirmationEnabled &&
                    this.setDurationEnabled &&
                    !this.isInfiniteBlock(this.block, this.isRepeating) &&
                    window.IS_LOCAL &&
                    isMobileOrTablet()
                );
            },

            canSetDuration() {
                if (BUILD_ELECTRON) {
                    return this.canChangeDuration;
                }

                if (!window.IS_LOCAL) {
                    return this.setDurationEnabled;
                }

                if (this.setDurationConfirmationEnabled) {
                    const isTablet = isMobileOrTablet();

                    if (!isTablet) {
                        return this.setDurationEnabled;
                    }

                    return this.canChangeDuration;
                }

                return this.setDurationEnabled;
            },

            canSetVolume() {
                if (!window.IS_LOCAL || this.communication === PLAYER_COMMUNICATION.INLINE) {
                    return false;
                }

                return this.companySettings?.volume_control_enabled;
            },

            ...mapState({
                companySettings: state => state.companySettings,
            }),
        },

        data() {
            return {
                dragX: null,
                canChangeDuration: false,
            };
        },

        mounted() {
            window.addEventListener('mousemove', this.dragging);
            window.addEventListener('mouseup', this.dragEnd);

            window.addEventListener('touchmove', this.dragging);
            window.addEventListener('touchend', this.dragEnd);
        },

        beforeDestroy() {
            window.removeEventListener('mousemove', this.dragging);
            window.removeEventListener('mouseup', this.dragEnd);

            window.removeEventListener('touchmove', this.dragging);
            window.removeEventListener('touchend', this.dragEnd);
        },

        methods: {
            blockActionDebounced: debounce(function(...args) {
                this.blockAction(...args);
            }, 1000, {
                leading: true,
                trailing: false,
            }),

            dragStart($event) {
                this.dragX = $event.clientX ? $event.clientX : $event.touches[0].pageX;
                console.log('Start drag', this.dragX);
            },

            dragging($event) {
                if (this.dragX === null) {
                    return;
                }

                this.dragX = $event.clientX ? $event.clientX : $event.touches[0].pageX;
            },

            dragEnd() {
                if (this.dragX === null) {
                    return;
                }

                this.changeDuration(this.dragDuration);

                this.dragX = null;
            },

            onProgressBarClick($event) {
                if (!this.setDurationEnabled || !this.canSetDuration) {
                    return;
                }

                const max = this.totalDuration;

                if (max === 0 && this.currentDuration === 0) {
                    return;
                }

                const offsetLeft = this.$refs.progressBar.offsetLeft;
                const width = this.$refs.progressBar.clientWidth;
                const mouseX = $event.clientX - offsetLeft;

                const barPercentage = (mouseX <= width ? mouseX : width) / (width / 100);

                if (barPercentage <= 0) {
                    this.changeDuration(0);
                    return;
                }

                this.changeDuration(Math.floor((max / 100) * barPercentage));
            },

            changeDuration(duration) {
                if (!this.setDurationEnabled || !this.canSetDuration) {
                    return;
                }

                this.blockAction('duration', duration);
                console.log('Set duration to', duration);
            },

            isInfiniteBlock: isInfiniteBlock,

            isPlaying() {
                return this.block && this.state === PLAYER_STATE.PLAYING;
            },

            isPaused() {
                return this.block && this.state === PLAYER_STATE.PAUSED;
            },

            async blockAction(action, duration = undefined) {
                switch (this.communication) {
                    case PLAYER_COMMUNICATION.WEBSOCKET:
                        try {
                            await this.$http.post('/api/player/blocks/' + action, {
                                id: this.block.id,
                                duration,
                            });
                        } catch (error) {
                            console.log(error);
                        }

                        return;
                    case PLAYER_COMMUNICATION.IPC:
                    case PLAYER_COMMUNICATION.INLINE:
                        let state = null;

                        switch (action) {
                            case 'resume':
                                state = PLAYER_STATE.PLAYING;
                                break;
                            case 'pause':
                                state = PLAYER_STATE.PAUSED;
                                break;
                            case 'stop':
                                state = PLAYER_STATE.STOPPED;
                                break;
                            case 'duration':
                                state = PLAYER_STATE.DURATION;
                                break;
                        }

                        this.emitAction('BlockStateChanged', {
                            state,
                            duration,
                        });

                        // When we stop we want to render the fixed block when configured.
                        if (state === PLAYER_STATE.STOPPED) {
                            if (this.fixedBlock && this.fixedBlock.id !== this.block.id) {
                                this.emitAction('BlockStarted', {
                                    block: {
                                        ...this.fixedBlock,
                                        ceremony: this.ceremony,
                                    },
                                    state: PLAYER_STATE.PLAYING,
                                });
                            }
                        }
                }
            },

            setVolume(volume) {
                this.$emit('set-volume', Math.min(100, Math.max(0, Number(volume))));
            },

            emitAction(action, data) {
                if (this.communication === PLAYER_COMMUNICATION.INLINE) {
                    EventBus.$emit(action, data);
                } else if (this.communication === PLAYER_COMMUNICATION.IPC) {
                    window.dispatchEvent(new CustomEvent('ipc-to-player', {
                        detail: {
                            action,
                            data,
                        },
                    }));

                    // Send event to the controller itself
                    window.dispatchEvent(new CustomEvent('ipc-from-player', {
                        detail: {
                            action,
                            data,
                        },
                    }));
                }
            },
        },

    };

</script>

<style lang="scss" scoped>
    .block-name {
        display: block;
        margin-top: -10px;
        margin-bottom: 10px;
        font-size: 20px;
        text-align: center;
    }

    .fixed-player-info {
        position: fixed;
        bottom: 0;
        left: 0;
        width: 100%;
        background-color: #fff;
        z-index: 999;
        padding: 30px;
        padding-top: 15px;
        box-shadow: 2px -2px 5px rgba(31, 45, 61, 0.2);

        .player-actions {
            text-align: center;
        }

        .playing-info {
            margin-left: auto;
            text-align: right;
        }

        .player-actions-info {
            margin-bottom: 15px;
        }
    }

    .player-progress-bar {
        display: flex;
        align-items: center;
        flex-direction: row;

        .progress-bar-text {
            width: 80px;
        }

        .progress-bar-start {
            padding-right: 10px;
            text-align: right;
        }

        .progress-bar-end {
            padding-left: 10px;
            text-align: left;
        }

        .progress-bar-visual {
            background-color: #EFF2F7;
            height: 5px;
            width: 100%;
            display: flex;
            border-radius: 5px;
            position: relative;

            &.set-duration-enabled {
                cursor: pointer;
            }

            .progress-bar-content {
                background: transparent;
                border-radius: 5px;
                width: 0;
                height: 100%;
                //transition: 2s ease width;
                position: absolute;

                .progress-bar-handle {
                    position: absolute;
                    top: 0;
                    right: 0;
                    margin-top: -2px;
                    margin-right: -4px;
                    width: 9px;
                    height: 9px;
                    border-radius: 50%;
                    cursor: pointer;

                    .progress-bar-handle-value {
                        pointer-events: none;
                        margin-top: -25px;
                        position: absolute;
                        width: 50px;
                        margin-left: -20px;
                        text-align: center;
                        user-select: none;
                    }

                    @media(max-width: 769px) {
                        margin-top: -5px;
                        margin-right: -7px;
                        width: 14px;
                        height: 14px;
                    }
                }

                &.progress-bar-content-dragging {
                    transition: none;
                }

                &.no-animation {
                    transition: none;
                }
            }
        }
    }

    .volume-control {
        position: absolute;
        right: 30px;
        top: 5px;
        z-index: 10;

        .btn {
            padding: 0.2rem 0.3rem;
            font-size: 0.55rem;
            line-height: 1;
            border-radius: 0.375rem
        }
    }
</style>
